import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Form, Input, FormFeedback, Label, Button, Alert, Spinner } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { registerUser, initializeUserProgress } from "../../helpers/firebaseAuth";
import { isValidPassword, PASSWORD_REQUIREMENTS } from "../../helpers/passwordValidation";
import { setAppSession } from "../../helpers/sessionScope";
import { ROLE_PM, ROLE_TENANT, ROLE_HOMES } from "../../context/RoleContext";
import RoleToggle from "./RoleToggle";

const RegisterForm = ({ navigate, loginWithGoogle, checkAuth, initialMode, onSuccess, onSignIn }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [role, setRole] = useState(initialMode === ROLE_PM ? ROLE_PM : ROLE_TENANT);

  const form = useFormik({
    initialValues: { email: "", username: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      username: Yup.string().required("Username is required"),
      password: Yup.string()
        .required("Password is required")
        .test("password-strength", PASSWORD_REQUIREMENTS, isValidPassword),
    }),
    onSubmit: async (values) => {
      setError("");
      setLoading(true);
      try {
        const user = await registerUser(values.email, values.password, values.username);
        await initializeUserProgress(user.uid);
        setAppSession();
        localStorage.setItem("ezron.activeRole", role);
        await checkAuth();
        if (onSuccess) {
          onSuccess();
          return;
        }
        navigate(`/verify-email?returnUrl=${encodeURIComponent(ROLE_HOMES[role])}`, { replace: true });
      } catch (err) {
        if (err?.code === "auth/email-already-in-use") {
          setError("An account with this email already exists.");
        } else if (err?.code === "auth/weak-password") {
          setError(PASSWORD_REQUIREMENTS);
        } else {
          setError("Registration failed. Please try again.");
        }
        setLoading(false);
      }
    },
  });

  const handleGoogleRegister = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
      setAppSession();
      localStorage.setItem("ezron.activeRole", role);
      await checkAuth();
      if (onSuccess) {
        onSuccess();
        return;
      }
      navigate(ROLE_HOMES[role]);
    } catch (err) {
      const code = err?.code;
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/popup-blocked"
      ) {
        setLoading(false);
        return;
      }
      setError("Google sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
        <p className="text-muted small mt-2 mb-0">Creating your account…</p>
      </div>
    );
  }

  return (
    <Form onSubmit={form.handleSubmit}>
      {error && (
        <Alert color="danger" className="mb-3 py-2 small">
          <i className="bx bx-error-circle me-2" />{error}
        </Alert>
      )}

      <RoleToggle role={role} onChange={setRole} />

      <div className="mb-3">
        <Label className="form-label small fw-semibold">Email</Label>
        <Input
          name="email"
          type="email"
          placeholder="you@example.com"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={form.values.email}
          invalid={form.touched.email && !!form.errors.email}
        />
        {form.touched.email && form.errors.email && (
          <FormFeedback>{form.errors.email}</FormFeedback>
        )}
      </div>

      <div className="mb-3">
        <Label className="form-label small fw-semibold">Username</Label>
        <Input
          name="username"
          type="text"
          placeholder="Your name"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={form.values.username}
          invalid={form.touched.username && !!form.errors.username}
        />
        {form.touched.username && form.errors.username && (
          <FormFeedback>{form.errors.username}</FormFeedback>
        )}
      </div>

      <div className="mb-3">
        <Label className="form-label small fw-semibold">Password</Label>
        <Input
          name="password"
          type="password"
          placeholder="••••••••"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={form.values.password}
          invalid={form.touched.password && !!form.errors.password}
        />
        {form.touched.password && form.errors.password && (
          <FormFeedback>{form.errors.password}</FormFeedback>
        )}
      </div>

      <Button color="primary" className="w-100 mt-1" type="submit">
        Create Account
      </Button>

      <div className="d-flex align-items-center gap-2 my-3">
        <hr className="flex-grow-1 my-0" />
        <span className="text-muted small">or</span>
        <hr className="flex-grow-1 my-0" />
      </div>

      <button
        type="button"
        className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2"
        onClick={handleGoogleRegister}
      >
        <i className="mdi mdi-google fs-5" /> Continue with Google
      </button>

      <p className="text-center text-muted small mt-4 mb-0">
        By creating an account you agree to Porcher&#39;s{" "}
        <Link to="/terms-of-service" className="text-primary">Terms of Service</Link>.
      </p>

      <p className="text-center text-muted small mt-3 mb-0">
        Already have an account?{" "}
        {onSignIn ? (
          <button type="button" className="btn btn-link p-0 align-baseline text-primary fw-semibold" onClick={onSignIn}>
            Sign in
          </button>
        ) : (
          <Link to="/login" className="text-primary fw-semibold">
            Sign in
          </Link>
        )}
      </p>
    </Form>
  );
};

export default RegisterForm;
